import type { AuthStrategy } from "./auth";

/**
 * The console's open organization, named on the Data API and Tile API requests its session
 * makes.
 *
 * The client takes no option for a header of the caller's own, and the one place a request can
 * gain one is the `fetch` it is handed (`ClientOptions.fetch`, the documented seam for proxies and
 * instrumentation). So this module is that `fetch`: every request reaching it was built by the
 * client, its credential, base URL, retry policy and error taxonomy already decided, and it leaves
 * through the global `fetch` with one header more. It makes no request of its own, which is why
 * it lives apart from `sdk.ts`, the one module that builds a client, and why the boundary gates
 * name it as the client's fetch rather than as a call site.
 */

/**
 * CONSOLE_ORGANIZATION_HEADER is the header the console names, on every Data API and Tile API
 * request it makes under its session, the organization its Map Studio has open.
 *
 * It decides one thing only: which organization the request is recorded under as `studio-online`
 * usage, which no limit, allowance or invoice counts. The API honours it while the signed-in
 * user is a member of that organization and records the request under their personal space
 * otherwise, or when the header is absent. It changes nothing a request is answered, may reach or
 * is bounded by, so a wrong or missing value costs a misattributed figure and nothing else. The
 * contract declares it on the console-only `ConsoleSession` scheme, which has no parameter to
 * generate a constant from, so it is spelled once here.
 */
export const CONSOLE_ORGANIZATION_HEADER = "X-Mountaya-Organization";

/**
 * NO_ORGANIZATION is the source a session answers before the application names one: no
 * organization, so the header is left off and the API records the usage under the personal
 * space.
 */
const NO_ORGANIZATION = (): string | null => null;

/**
 * organizationSource answers, at the moment a request leaves, the organization the console has
 * open. It is module state for the reason `requestLanguage` is, and a SOURCE rather than a value
 * because the answer is the router's: read at request time it is always the page in view, where a
 * value pushed from an effect would reach a map's first tiles a commit late, effects running
 * children first.
 */
let organizationSource: () => string | null = NO_ORGANIZATION;

/**
 * setRequestOrganizationSource records where the console reads the organization it has open:
 * the application registers its router once, and a null source returns the module to naming
 * none. The api package is an architectural leaf and cannot read the router, so the answer is
 * pushed in as a function rather than read out.
 */
export const setRequestOrganizationSource = (source: (() => string | null) | null): void => {
  organizationSource = source ?? NO_ORGANIZATION;
};

/**
 * getRequestOrganization returns the organization the console has open now, or null when it has
 * none (the personal space, a page outside any organization, or no source registered).
 */
export const getRequestOrganization = (): string | null => organizationSource();

/**
 * TRAILING_SLASHES matches the slashes a base URL may end on, which a request under it repeats
 * once.
 */
const TRAILING_SLASHES = /\/+$/;

/**
 * isUnderBase reports whether a request URL is bound for a service at a base URL: the base itself,
 * or a path under it at a segment boundary. A path base is read on the page's own origin, where
 * the transport sends it; an empty one is the page's origin whole, which cannot tell one service
 * from another, so it claims nothing.
 */
const isUnderBase = (url: string, base: string): boolean => {
  const trimmed = base.replace(TRAILING_SLASHES, "");
  if (trimmed === "") {
    return false;
  }

  const absolute =
    trimmed.startsWith("/") && typeof location !== "undefined"
      ? `${location.origin}${trimmed}`
      : trimmed;

  return url === absolute || url.startsWith(`${absolute}/`) || url.startsWith(`${absolute}?`);
};

/**
 * requestUrlOf spells whatever `fetch` was handed as the URL it fetches.
 */
const requestUrlOf = (input: RequestInfo | URL): string => {
  if (typeof input === "string") {
    return input;
  }

  return input instanceof URL ? input.href : input.url;
};

/**
 * organizationFetch is the `fetch` a console session client sends through: a request bound for
 * one of the given bases (the Data API's, the Tile API's) carries the organization the console
 * has open in {@link CONSOLE_ORGANIZATION_HEADER}, and every other request leaves as it came.
 *
 * The global `fetch` is read at each call, as the client's own default is, so a test stubbing
 * it reaches every client already built.
 */
const organizationFetch =
  (bases: readonly string[]): typeof fetch =>
  (input, init) => {
    const organizationId = getRequestOrganization();
    if (organizationId === null || !bases.some((base) => isUnderBase(requestUrlOf(input), base))) {
      return globalThis.fetch(input, init);
    }

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    headers.set(CONSOLE_ORGANIZATION_HEADER, organizationId);

    return globalThis.fetch(input, { ...init, headers });
  };

/**
 * sessionFetchFor returns the `fetch` option a client is built with: for the console session, the
 * one that names the open organization on the Data API and the Tile API bases it reaches; for a
 * key or a share token, nothing, since the header is the console session's alone.
 */
export const sessionFetchFor = (
  strategy: AuthStrategy,
  bases: readonly (string | undefined)[],
): { fetch?: typeof fetch } => {
  const reached = bases.filter((base): base is string => base !== undefined && base !== "");

  return strategy.kind === "session" && reached.length > 0
    ? { fetch: organizationFetch(reached) }
    : {};
};
